import { useEffect, useRef, useState, type HTMLAttributes, type ImgHTMLAttributes, type CSSProperties } from 'react';
// styles.css imports imageLoading.css for all hosts, including server-rendered evidence.

export interface ImageLoadingProps extends HTMLAttributes<HTMLSpanElement> {
  label?: string;
}

/** Spinner-only skeleton; the accessible label describes capture, upload or image download. */
export function ImageLoading({ label = 'Loading image…', className = '', ...props }: ImageLoadingProps) {
  return <span {...props} className={`oq-image-loading ${className}`} role="status" aria-label={label}>
    <svg className="oq-image-loading__spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="oq-image-loading__track" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>;
}

export interface ImageWithLoaderProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** True while capture/upload/URL resolution has not produced an image yet. */
  pending?: boolean;
  /** An upstream URL resolver failed before an <img> could be mounted. */
  failed?: boolean;
  /** Fill an existing positioned crop frame without changing its coordinate system. */
  fill?: boolean;
  /** Thumbnail slots show a spinner or compact error mark with an accessible full label. */
  compact?: boolean;
  frameClassName?: string;
  frameStyle?: CSSProperties;
  loadingLabel?: string;
  errorLabel?: string;
}

function ImageContent({ pending, failed, fill, compact, frameClassName = '', frameStyle, loadingLabel = 'Loading image…',
  errorLabel = 'Image unavailable', onLoad, onError, ...props }: ImageWithLoaderProps) {
  const image = useRef<HTMLImageElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    const element = image.current;
    if (!element || pending) return;
    let active = true;
    const ready = async () => {
      if (!element.naturalWidth) return;
      // Wait for pixels, not just the URL or HTTP response. Cached images may already be complete on mount.
      try { await element.decode?.(); } catch { /* A loaded image may not support decode (for example SVG). */ }
      if (active) setState('ready');
    };
    const failed = () => { if (active) setState('error'); };
    element.addEventListener('load', ready);
    element.addEventListener('error', failed);
    if (element.complete) {
      if (element.naturalWidth) void ready();
      else if (element.getAttribute('src') || element.getAttribute('srcset')) failed();
    }
    return () => { active = false; element.removeEventListener('load', ready); element.removeEventListener('error', failed); };
  }, [props.src, pending]);
  const currentState = failed || (!pending && !props.src && !props.srcSet) ? 'error' : state;
  const loading = currentState !== 'error' && (pending || state === 'loading');
  return <span className={`oq-image-frame${fill ? ' oq-image-frame--fill' : ''}${compact ? ' oq-image-frame--compact' : ''} ${frameClassName}`}
    style={frameStyle} data-image-state={loading ? 'loading' : currentState} aria-busy={loading}>
    {(props.src || props.srcSet) && <img {...props} ref={image} onLoad={onLoad} onError={onError} />}
    {loading && <ImageLoading label={loadingLabel} />}
    {!loading && currentState === 'error' && <span className="oq-image-loading oq-image-loading--error" role="status" aria-label={errorLabel} title={errorLabel}>
      <span className="oq-image-loading__error-label" aria-hidden="true">{errorLabel}</span>
      <span className="oq-image-loading__error-mark" aria-hidden="true">!</span>
    </span>}
  </span>;
}

/** Source identity resets loading/errors synchronously; a late load from the old image cannot clear the new one. */
export function ImageWithLoader(props: ImageWithLoaderProps) {
  return <ImageContent key={`${props.src ?? ''}\n${props.srcSet ?? ''}`} {...props} />;
}
